import { ComponentProps, useEffect } from 'react';
import { defaultSupportedDynamicStyleIds, mapId } from '../../utils/constants';
import use3dMap from '../../hooks/use3dMap';
import useBaseStyle from '../../hooks/useBaseStyle';
import { useDispatch, useSelector } from '../../redux';
import { updateSupportedDynamicStyleIds } from '../../redux/reducer';
import { DynamicStyleId } from '../../types/Styles';
import BaseMapReact from './BaseMapReact';

type BaseMapProps = Omit<
  ComponentProps<typeof BaseMapReact>,
  'baseMapStyle' | 'cursor' | 'fog' | 'id' | 'is3dActive' | 'staticStyleIds' | 'terrain'
> & {
  ignoreInitial3dPitch?: boolean;
  supportedDynamicStyleIds?: DynamicStyleId[];
};

/**
 * BaseMap represents our standard customization around our 3rd party map
 * vendor's Map API. We set our typical settings, add our controls, and handle
 * complex functionality specific to maps here and in the appropriate
 * sub-components.
 *
 * @param {props}: BaseMapProps
 * @returns
 */

const BaseMap = ({
  children,
  controlsConfig,
  ignoreInitial3dPitch,
  supportedDynamicStyleIds = defaultSupportedDynamicStyleIds,
  ...props
}: BaseMapProps) => {
  const dispatch = useDispatch();
  const { cursor, is3dActive, staticStyleIds } = useSelector(state => ({
    cursor: state.map.cursor,
    is3dActive: state.map.is3dActive,
    staticStyleIds: state.map.staticStyleIds
  }));

  const baseMapStyle = useBaseStyle();
  const { styles: map3DBaseStyles } = use3dMap(is3dActive, ignoreInitial3dPitch);

  useEffect(() => {
    if (supportedDynamicStyleIds) {
      dispatch(updateSupportedDynamicStyleIds(supportedDynamicStyleIds));
    }
  }, [dispatch, supportedDynamicStyleIds]);

  return (
    <>
      {/* don't render the map until we have our base styles from mapbox 
      we need the glyphs from the style in order to render the cluster marker text
      */}
      {baseMapStyle && (
        <BaseMapReact
          {...props}
          baseMapStyle={baseMapStyle}
          cursor={cursor}
          id={mapId}
          is3dActive={is3dActive}
          staticStyleIds={staticStyleIds}
          {...map3DBaseStyles}
          {...(controlsConfig || {})}
        >
          {children}
        </BaseMapReact>
      )}
    </>
  );
};

export default BaseMap;
